import { UserAccessOperators } from "gql/generated";
import { Plus } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { PageContent } from "shared/components/layouts";
import { css } from "styled-system/css";
import { Grid } from "styled-system/jsx";
import { StaffCard } from "../StaffCard";
import { InviteStaffModal } from "../InviteStaffModal";
import {
  useStaffPageContent,
  UseStaffPageContentProps,
} from "./useStaffPageContent";
import { Dispatch, SetStateAction, useEffect } from "react";
import { SimpleDialog } from "@ttc3k/trekker";
import { TagAction } from "../StaffCard/useStaffCard";

type StaffPageContentProps = {
  setInviteStaffModalClickHandler?: ({
    trigger,
  }: {
    trigger: () => void;
  }) => void;
  viewOnly?: boolean;
  forceSingleColumn?: boolean;
  setStaffCount?: Dispatch<SetStateAction<number>>;
} & UseStaffPageContentProps;

export const StaffPageContent = ({
  _entityId,
  type,
  viewOnly = false,
  forceSingleColumn = false,
  setInviteStaffModalClickHandler,
  staffGetter,
  setStaffCount,
}: StaffPageContentProps) => {
  const { t } = useTranslation();

  const {
    staff,
    error,
    loading,
    updatingId,
    confirmModalInfo,
    isConfirmationModalOpen,
    isInviteStaffModalOpen,
    handleAcceptClick,
    handleInviteStaffModalClick,
    handleInviteStaffClick,
    handleOpenConfirmationModal,
    handleConfirmationModalActionClick,
    handleCreateOrResendInviteClick,
  } = useStaffPageContent({ _entityId, type, staffGetter });

  useEffect(() => {
    setInviteStaffModalClickHandler?.({
      trigger: handleInviteStaffModalClick,
    });
  }, [handleInviteStaffModalClick, setInviteStaffModalClickHandler]);

  useEffect(() => setStaffCount?.(staff?.length ?? 0), [staff, setStaffCount]);

  return (
    <>
      <PageContent
        loading={loading}
        showSimpleError={!staff}
        throwAppError={
          error && (error as unknown as { message: string })?.message
        }
        showEmptyStateWithProps={
          !staff || staff.length === 0
            ? {
                className: css({
                  p: viewOnly ? "400" : "800",
                  height: viewOnly ? "auto" : "300px",
                }),
                text: viewOnly ? t("core:STAFF.DESCRIPTION") : "",
                ctaButtonProps: !viewOnly
                  ? {
                      Icon: Plus,
                      visual: "accent.blue",
                      mode: "light",
                      onClick: handleInviteStaffModalClick,
                      children: t("core:STAFF.CTA_BUTTON.INVITE"),
                    }
                  : undefined,
              }
            : undefined
        }
      >
        <Grid
          gap={"250"}
          gridTemplateColumns={
            forceSingleColumn
              ? "1fr"
              : {
                  base: "1fr",
                  md: "1fr 1fr",
                  xl: "1fr 1fr 1fr",
                }
          }
        >
          {staff?.map((staffMember) => (
            <StaffCard
              key={staffMember._id}
              _entityId={_entityId}
              type={type}
              staffMember={staffMember}
              accessTo={
                staffMember.access.organizations as UserAccessOperators[]
              }
              viewOnly={viewOnly}
              onAcceptClick={() =>
                handleAcceptClick(staffMember._id, staffMember.email)
              }
              onRemoveClick={(action: TagAction) =>
                handleOpenConfirmationModal(
                  action,
                  staffMember._id,
                  staffMember.email,
                )
              }
              onResendInviteClick={() =>
                handleCreateOrResendInviteClick(
                  {
                    email: staffMember.email,
                    firstName: staffMember.firstName,
                    lastName: staffMember.lastName,
                  },
                  false,
                  true, // When resending an invite, we always send an email
                )
              }
              onSendInviteClick={() =>
                handleCreateOrResendInviteClick(
                  {
                    email: staffMember.email,
                    firstName: staffMember.firstName,
                    lastName: staffMember.lastName,
                  },
                  true,
                  true, // When sending an invite, we always send an email
                )
              }
              updatingId={updatingId}
            />
          ))}
        </Grid>
      </PageContent>
      <InviteStaffModal
        entityId={_entityId}
        isOpen={isInviteStaffModalOpen}
        onClose={handleInviteStaffModalClick}
        onInviteStaffClick={handleInviteStaffClick}
      />
      <SimpleDialog
        open={isConfirmationModalOpen}
        title={t("core:STAFF.CONFIRM_MODAL.TITLE")}
        message={t(`core:STAFF.CONFIRM_MODAL.${confirmModalInfo?.action}`)}
        cancelButtonCopy={t("core:CONFIRM.NO")}
        onCancelClick={() => handleConfirmationModalActionClick(false)}
        confirmButtonCopy={t("core:CONFIRM.YES")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => handleConfirmationModalActionClick(true)}
        size="md"
      />
    </>
  );
};
